import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { FiArrowLeft, FiFolder, FiUpload, FiExternalLink } from 'react-icons/fi';
import { FaBox } from 'react-icons/fa';

const BoxPage: React.FC = () => {
  const navigate = useNavigate();

  // Mock data
  const storageUsed = 2.4; // GB
  const storageTotal = 50; // GB
  const usagePercentage = (storageUsed / storageTotal) * 100;

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center text-uc-navy hover:text-uc-gold transition-colors mb-4"
          >
            <FiArrowLeft size={20} className="mr-2" />
            Back to Home
          </button>
          <h1 className="text-3xl font-bold text-uc-navy font-header">BOX CLOUD STORAGE</h1>
          <p className="text-gray-600 mt-2">Access and manage your cloud files</p>
        </div>

        {/* Storage Overview */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <FaBox className="text-uc-gold" size={32} />
            <h2 className="text-xl font-bold text-uc-navy">Storage Overview</h2>
          </div>

          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-700">Storage Used</span>
                <span className="text-uc-navy font-semibold">{storageUsed} GB of {storageTotal} GB</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-uc-gold h-3 rounded-full transition-all"
                  style={{ width: `${usagePercentage}%` }}
                ></div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 pt-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-600 text-sm">Files</p>
                <p className="text-2xl font-bold text-uc-navy mt-1">127</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-600 text-sm">Folders</p>
                <p className="text-2xl font-bold text-uc-navy mt-1">18</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-600 text-sm">Shared</p>
                <p className="text-2xl font-bold text-uc-navy mt-1">6</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-uc-navy mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <FiUpload className="text-uc-gold" size={24} />
              <div className="text-left">
                <p className="font-semibold text-uc-navy">Upload Files</p>
                <p className="text-sm text-gray-600">Add files to your Box account</p>
              </div>
            </button>
            <button className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <FiFolder className="text-uc-gold" size={24} />
              <div className="text-left">
                <p className="font-semibold text-uc-navy">Create Folder</p>
                <p className="text-sm text-gray-600">Organize your files</p>
              </div>
            </button>
          </div>
        </div>

        {/* Access Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <FiExternalLink className="text-blue-600 mt-1 flex-shrink-0" size={24} />
            <div className="flex-1">
              <h3 className="font-bold text-blue-900 mb-2">Access Full Box Account</h3>
              <p className="text-blue-800 mb-4">
                For full functionality and file management, access your Box account through the official Box website.
              </p>
              <a
                href="https://ucmerced.app.box.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                Open Box <FiExternalLink size={18} />
              </a>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="bg-white rounded-lg shadow p-6 mt-6">
          <h3 className="font-bold text-uc-navy mb-4">About Box at UC Merced</h3>
          <div className="space-y-3 text-gray-700">
            <p>
              Box is UC Merced's enterprise cloud storage solution. Features include:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>50 GB of secure cloud storage</li>
              <li>File sharing and collaboration tools</li>
              <li>Access from any device</li>
              <li>Integration with Office 365</li>
              <li>Version control and file recovery</li>
              <li>Mobile apps for iOS and Android</li>
            </ul>
            <p className="text-sm text-gray-600 mt-4">
              For technical support or questions about Box, contact IT Support at it@ucmerced.edu or visit the IT Service Desk.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BoxPage;
